import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, RefreshCw, Save, Smartphone } from "lucide-react";
import { getDeliveryConfig, updateDeliveryConfig } from "../../api/settings";
import { showToast } from "../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../utils/apiErrorMessage";
import { SectionCard } from "../ui/SectionCard";
import { Switch } from "../ui/Switch";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";

// ─────────────────────────────────────────────────────────────────────────────
// DriverAppDisplayConfig — Qué datos ve el repartidor en las cards de pedidos de
// la app (hoy: el nombre del comercio).
//
// Sección `driverAppDisplay` del artefacto DeliveryConfig (GET/PATCH
// /configuration/delivery). Autocontenida igual que DriverWorkflowConfig: el
// backend resuelve el operador dueño desde el JWT y mergea POR SECCIÓN.
//
// Apagado por defecto: sin la sección guardada la app no muestra el comercio, así
// que acá se lee `=== true` igual que el BFF al armar la config de la app.
// ─────────────────────────────────────────────────────────────────────────────

function showCommerceNameFromConfig(config) {
  return config?.driverAppDisplay?.showCommerceName === true;
}

/**
 * Texto a mostrar cuando falla el PATCH. El BFF responde 403 con un mensaje propio
 * y 400 con los mensajes de class-validator; los 5xx y los fallos de red no
 * exponen texto interno.
 */
function saveErrorMessage(err, t) {
  const fallback = t("No se pudo guardar la configuración");
  const status = apiErrorStatus(err);
  if (status === 403) {
    return apiErrorMessage(
      err,
      t("No tienes permiso para modificar la configuración de entrega."),
    );
  }
  if (status === null || status >= 500) return fallback;
  return apiErrorMessage(err, fallback);
}

export default function DriverAppDisplayConfig() {
  const { t } = useTranslation();

  const [showCommerceName, setShowCommerceName] = useState(false);
  const [snapshot, setSnapshot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getDeliveryConfig()
      .then((res) => {
        if (!active) return;
        const next = showCommerceNameFromConfig(res?.data ?? res);
        setShowCommerceName(next);
        setSnapshot(next);
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else {
          // No se muestra el interruptor con el default: Guardar pisaría la
          // config real del operador.
          setLoadError(true);
          showToast(t("No se pudo cargar la configuración de la app de repartidores"), false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `t` fuera de las deps a propósito: su identidad cambia con el idioma y eso
    // refetchearía la config, descartando ediciones sin aviso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const dirty = useMemo(() => showCommerceName !== snapshot, [showCommerceName, snapshot]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // La sección viaja completa; el backend mergea por sección.
      const res = await updateDeliveryConfig({ driverAppDisplay: { showCommerceName } });
      // El BFF responde el documento guardado. Uno que no conoce la sección la
      // descarta en silencio (whitelist del ValidationPipe) y responde 200 igual: se
      // muestra lo que quedó guardado, no lo enviado.
      const saved = showCommerceNameFromConfig(res?.data ?? res);
      setShowCommerceName(saved);
      setSnapshot(saved);
      if (saved === showCommerceName) {
        showToast(t("Configuración de la app de repartidores guardada"), true);
      } else {
        showToast(t("El cambio no quedó guardado. Inténtalo de nuevo más tarde."), false);
      }
    } catch (err) {
      showToast(saveErrorMessage(err, t), false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setShowCommerceName(snapshot);

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("Sin configuración disponible")}
        subtitle={t(
          "Tu usuario no pertenece a un operador con configuración de entrega.",
        )}
      />
    );
  }

  if (loadError) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("No se pudo cargar la configuración")}
        subtitle={t("Reintenta en un momento.")}
        action={
          <Button
            variant="outline"
            onClick={() => setReloadKey((k) => k + 1)}
            iconLeft={<RefreshCw size={16} />}
          >
            {t("Reintentar")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={<Smartphone className="w-4 h-4" />}
        title={t("Pedidos en la app de repartidores")}
        subtitle={t("Qué ve tu equipo en cada pedido de la app de repartidores.")}
      >
        <div className="flex flex-col gap-2">
          <Switch
            id="driver-app-show-commerce-name"
            checked={showCommerceName}
            onChange={setShowCommerceName}
            disabled={saving}
            label={t("Mostrar el nombre del comercio")}
            aria-describedby="driver-app-show-commerce-name-help"
          />
          <p id="driver-app-show-commerce-name-help" className="text-xs text-fg-subtle">
            {t(
              "El repartidor ve a qué comercio pertenece cada pedido en la lista de entregas, en su ruta y en el detalle del pedido.",
            )}
          </p>
          <p className="flex items-start gap-1.5 text-xs text-fg-muted">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            {t("Requiere la última actualización de la app de repartidores.")}
          </p>
        </div>
      </SectionCard>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
          {t("Descartar cambios")}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!dirty || saving}
          iconLeft={!saving ? <Save size={16} /> : undefined}
        >
          {saving ? t("Guardando…") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
